import { AbstractControl } from '@angular/forms';

/**
 * Mapa de chave de erro de validador (`control.errors`) para a mensagem exibida
 * em `Field Error Displayed`. Cobre os validators de `custom-validators.ts`
 * usados hoje em `myaccount`.
 *
 * Vários validators do projeto devolvem a chave de erro com valor `false`, não
 * `true` (ex.: `{ fullName: false }`, `{ validCNPJ: false }`), e `validCPF`
 * devolve `true` num ramo e `false` noutro. Por isso a checagem abaixo usa
 * presença da chave (`key in errors`), nunca o valor.
 *
 * `required`, `duplicateCpf` e `duplicateName` batem com o dicionário
 * compartilhado `errors.js`. As demais são o texto canônico deste mapa, não a
 * transcrição de uma tela: a mesma chave pode renderizar mensagens diferentes
 * dependendo de onde o campo é exibido — `fullName` sai como "Nome inválido"
 * via `errors.js` e como "Informe o nome completo do cônjuge." no span fixo de
 * `name-edit.component.html`. Validar com o time de conteúdo antes do rollout.
 */
const FIELD_ERROR_MESSAGES: Record<string, string> = {
    required: 'Campo obrigatório',
    pattern: 'Formato inválido',
    minlength: 'Quantidade de caracteres insuficiente',
    maxlength: 'Quantidade de caracteres excedida',
    fullName: 'Informe o nome completo',
    duplicateName: 'Nome deve ser diferente do seu',
    duplicateCpf: 'CPF deve ser diferente do seu',
    validCPF: 'CPF inválido',
    validCNPJ: 'CNPJ inválido',
    validCNH: 'Número da CNH inválido',
    phoneError: 'Celular inválido',
    invalidDate: 'Data inválida',
    dateValid: 'Data inválida',
    validDateToBirthday: 'Data de nascimento inválida',
    underAge: 'Você precisa ser maior de idade',
    minvalue: 'Valor mínimo não atingido',
    notUnique: 'Valor já utilizado',
    atLeaseOne: 'Selecione ao menos uma opção',
    checked: 'Confirmação obrigatória',
    customSpecialCharacterValidation: 'Informe o nome completo',
    validateMaxCurrencyNumber: 'Valor máximo excedido',
    validateNegativeValue: 'Valor não pode ser negativo',
    passwordMismatch: 'As senhas não coincidem',
};

const DEFAULT_MESSAGE = FIELD_ERROR_MESSAGES.required;

/**
 * Resolve a mensagem de erro real de um control para o `Text Error` de
 * `Field Error Displayed`, a partir da primeira chave conhecida em
 * `control.errors`. Cai para `'Campo obrigatório'` quando o control não tem
 * erros ou nenhuma chave é reconhecida (mesmo comportamento de antes desta
 * função existir).
 */
export function getFieldErrorMessage(control: AbstractControl | null | undefined, preferredKey?: string): string {
    const errors = control && control.errors;

    if (!errors) {
        return DEFAULT_MESSAGE;
    }

    if (preferredKey && preferredKey in errors && FIELD_ERROR_MESSAGES[preferredKey]) {
        return FIELD_ERROR_MESSAGES[preferredKey];
    }

    const knownKey = Object.keys(FIELD_ERROR_MESSAGES).find((key) => key in errors);

    return knownKey ? FIELD_ERROR_MESSAGES[knownKey] : DEFAULT_MESSAGE;
}

/**
 * Retorna o "error_type" (chave do primeiro erro de validação) de um controle,
 * sem expor o valor digitado pelo usuário (payload sem PII).
 */
export function getFieldErrorType(control: AbstractControl | null | undefined): string | null {
    if (!control || !control.errors) {
        return null;
    }
    const keys = Object.keys(control.errors);
    return keys.length ? keys[0] : null;
}
